"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  CheckCircle,
  AlertTriangle,
  Info,
  ChevronRight,
  ChevronLeft,
  Users,
  Calendar,
  Heart,
  Bone,
  ArrowLeft,
  MessageCircle,
  X,
} from "lucide-react";
import BookingForm from "../booking-form";
import { useTranslation } from "@/hooks/useTranslation";

interface Question {
  id: number;
  question: string;
  options: { value: string; label: string; score: number }[];
}

interface Result {
  score: number;
  title: string;
  description: string;
  recommendation: string;
  additionalInfo?: string[];
}

export default function CandidateSection() {
  const { t, isRTL } = useTranslation("all-on-4");
  const [showQuiz, setShowQuiz] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<Result | null>(null);

  const eligibilityFactors = [
    {
      icon: <Calendar className="w-6 h-6 text-gold" />,
      label: t("candidates.eligibility.missingTeeth.title"),
      description: t("candidates.eligibility.missingTeeth.description"),
    },
    {
      icon: <Heart className="w-6 h-6 text-gold" />,
      label: t("candidates.eligibility.goodHealth.title"),
      description: t("candidates.eligibility.goodHealth.description"),
    },
    {
      icon: <Bone className="w-6 h-6 text-gold" />,
      label: t("candidates.eligibility.boneStructure.title"),
      description: t("candidates.eligibility.boneStructure.description"),
    },
    {
      icon: <Users className="w-6 h-6 text-gold" />,
      label: t("candidates.eligibility.oralHygiene.title"),
      description: t("candidates.eligibility.oralHygiene.description"),
    },
  ];

  const questions: Question[] = [
    {
      id: 1,
      question: t("candidates.quiz.questions.missingTeeth.question"),
      options: [
        {
          value: "few",
          label: t("candidates.quiz.questions.missingTeeth.options.few"),
          score: 6,
        },
        {
          value: "many",
          label: t("candidates.quiz.questions.missingTeeth.options.many"),
          score: 8,
        },
        {
          value: "most",
          label: t("candidates.quiz.questions.missingTeeth.options.most"),
          score: 10,
        },
        {
          value: "all",
          label: t("candidates.quiz.questions.missingTeeth.options.all"),
          score: 10,
        },
      ],
    },
    {
      id: 2,
      question: t("candidates.quiz.questions.dentures.question"),
      options: [
        {
          value: "no",
          label: t("candidates.quiz.questions.dentures.options.no"),
          score: 8,
        },
        {
          value: "partial",
          label: t("candidates.quiz.questions.dentures.options.partial"),
          score: 8,
        },
        {
          value: "full-comfortable",
          label: t(
            "candidates.quiz.questions.dentures.options.fullComfortable"
          ),
          score: 6,
        },
        {
          value: "full-problems",
          label: t("candidates.quiz.questions.dentures.options.fullProblems"),
          score: 10,
        },
      ],
    },
    {
      id: 3,
      question: t("candidates.quiz.questions.concerns.question"),
      options: [
        {
          value: "eating",
          label: t("candidates.quiz.questions.concerns.options.eating"),
          score: 10,
        },
        {
          value: "appearance",
          label: t("candidates.quiz.questions.concerns.options.appearance"),
          score: 8,
        },
        {
          value: "comfort",
          label: t("candidates.quiz.questions.concerns.options.comfort"),
          score: 9,
        },
        {
          value: "all",
          label: t("candidates.quiz.questions.concerns.options.all"),
          score: 10,
        },
      ],
    },
    {
      id: 4,
      question: t("candidates.quiz.questions.health.question"),
      options: [
        {
          value: "excellent",
          label: t("candidates.quiz.questions.health.options.excellent"),
          score: 10,
        },
        {
          value: "good",
          label: t("candidates.quiz.questions.health.options.good"),
          score: 8,
        },
        {
          value: "fair",
          label: t("candidates.quiz.questions.health.options.fair"),
          score: 6,
        },
        {
          value: "poor",
          label: t("candidates.quiz.questions.health.options.poor"),
          score: 3,
        },
      ],
    },
  ];

  const getResult = (totalScore: number): Result => {
    if (totalScore >= 32) {
      return {
        score: totalScore,
        title: t("candidates.quiz.results.excellent.title"),
        description: t("candidates.quiz.results.excellent.description"),
        recommendation: t("candidates.quiz.results.excellent.recommendation"),
        additionalInfo: Array.isArray(
          t("candidates.quiz.results.excellent.additionalInfo", {
            returnObjects: true,
          })
        )
          ? (t("candidates.quiz.results.excellent.additionalInfo", {
              returnObjects: true,
            }) as string[])
          : [],
      };
    } else if (totalScore >= 24) {
      return {
        score: totalScore,
        title: t("candidates.quiz.results.good.title"),
        description: t("candidates.quiz.results.good.description"),
        recommendation: t("candidates.quiz.results.good.recommendation"),
        additionalInfo: Array.isArray(
          t("candidates.quiz.results.good.additionalInfo", {
            returnObjects: true,
          })
        )
          ? (t("candidates.quiz.results.good.additionalInfo", {
              returnObjects: true,
            }) as string[])
          : [],
      };
    } else if (totalScore >= 16) {
      return {
        score: totalScore,
        title: t("candidates.quiz.results.possible.title"),
        description: t("candidates.quiz.results.possible.description"),
        recommendation: t("candidates.quiz.results.possible.recommendation"),
        additionalInfo: Array.isArray(
          t("candidates.quiz.results.possible.additionalInfo", {
            returnObjects: true,
          })
        )
          ? (t("candidates.quiz.results.possible.additionalInfo", {
              returnObjects: true,
            }) as string[])
          : [],
      };
    } else {
      return {
        score: totalScore,
        title: t("candidates.quiz.results.consultation.title"),
        description: t("candidates.quiz.results.consultation.description"),
        recommendation: t(
          "candidates.quiz.results.consultation.recommendation"
        ),
        additionalInfo: Array.isArray(
          t("candidates.quiz.results.consultation.additionalInfo", {
            returnObjects: true,
          })
        )
          ? (t("candidates.quiz.results.consultation.additionalInfo", {
              returnObjects: true,
            }) as string[])
          : [],
      };
    }
  };

  const handleAnswer = (value: string) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion]: value }));
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      calculateResult();
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const calculateResult = () => {
    const totalScore = questions.reduce((sum, question) => {
      const answer = answers[question.id - 1];
      const option = question.options.find((opt) => opt.value === answer);
      return sum + (option?.score || 0);
    }, 0);

    setResult(getResult(totalScore));
    setShowResult(true);
  };

  const resetQuiz = () => {
    setShowQuiz(false);
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
    setResult(null);
  };

  const getResultColor = (title: string) => {
    switch (title) {
      case "Excellent Candidate":
        return "text-green-600 dark:text-green-400";
      case "Good Candidate":
        return "text-blue-600 dark:text-blue-400";
      case "Possible Candidate":
        return "text-yellow-600 dark:text-yellow-400";
      default:
        return "text-orange-600 dark:text-orange-400";
    }
  };

  const getResultIcon = (title: string) => {
    switch (title) {
      case "Excellent Candidate":
        return <CheckCircle className="w-8 h-8 text-green-600" />;
      case "Good Candidate":
        return <CheckCircle className="w-8 h-8 text-blue-600" />;
      case "Possible Candidate":
        return <Info className="w-8 h-8 text-yellow-600" />;
      default:
        return <AlertTriangle className="w-8 h-8 text-orange-600" />;
    }
  };

  const handleConsultationClick = () => {
    setShowBooking(true);
  };

  return (
    <section
      id="candidates"
      className="py-16 lg:py-24 bg-slate-50 dark:bg-navy-950"
    >
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
            {(() => {
              const title = t("candidates.title");
              const highlightWords = [
                "Candidate",
                "Candidat",
                "Kandidat",
                "Candidato",
                "Candidato",
                "Кандидат",
                "مرشح",
                "کاندید",
              ];

              for (const word of highlightWords) {
                if (title.includes(word)) {
                  const parts = title.split(word);
                  return (
                    <>
                      {parts[0]}
                      <span className="text-gold">{word}</span>
                      {parts[1]}
                    </>
                  );
                }
              }
              return title;
            })()}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto mb-8">
            {t("candidates.subtitle")}
          </p>
        </div>

        {!showQuiz && !showResult && (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {eligibilityFactors.map((factor, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full border-slate-200 dark:border-slate-700 hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                        {factor.icon}
                      </div>
                      <h4 className="text-lg font-semibold text-navy dark:text-white mb-3">
                        {factor.label}
                      </h4>
                      <p className="text-slate-600 dark:text-slate-300 text-sm">
                        {factor.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
              <Button
                onClick={() => setShowQuiz(true)}
                className="bg-gold hover:bg-gold-600 text-navy font-semibold px-8 py-3 rounded-lg text-lg"
              >
                {t("candidates.buttons.takeAssessment")}
              </Button>
              <Button
                onClick={handleConsultationClick}
                variant="outline"
                className="border-gold text-gold hover:bg-gold hover:text-navy px-8 py-3 rounded-lg text-lg"
              >
                {t("candidates.buttons.bookConsultation")}
              </Button>
            </div>
          </>
        )}

        {/* Quiz/Result/Booking Container with Fixed Height */}
        <AnimatePresence>
          {(showQuiz || showResult || showBooking) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="max-w-2xl mx-auto"
            >
              <Card className="border-gold border-2 min-h-[700px]">
                <CardContent className="p-8">
                  {/* Quiz State */}
                  {showQuiz && !showResult && !showBooking && (
                    <>
                      <div className="flex justify-between items-center mb-6">
                        <span className="text-sm font-medium text-gold">
                          {t("candidates.quiz.progress", {
                            current: currentQuestion + 1,
                            total: questions.length,
                          })}
                        </span>
                        <button
                          onClick={resetQuiz}
                          className="text-slate-600 hover:text-gold transition-colors"
                        >
                          <ArrowLeft className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 mb-8">
                        <div
                          className="bg-gold h-2 rounded-full transition-all duration-300"
                          style={{
                            width: `${
                              ((currentQuestion + 1) / questions.length) * 100
                            }%`,
                          }}
                        />
                      </div>

                      <h3 className="text-xl font-bold text-navy dark:text-white mb-6">
                        {questions[currentQuestion].question}
                      </h3>

                      <div className="space-y-3 mb-8">
                        {questions[currentQuestion].options.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleAnswer(option.value)}
                            className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 ${
                              answers[currentQuestion] === option.value
                                ? "border-gold bg-gold/10"
                                : "border-slate-200 dark:border-slate-700 hover:border-gold/50"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-navy dark:text-white">
                                {option.label}
                              </span>
                              {answers[currentQuestion] === option.value && (
                                <CheckCircle className="w-5 h-5 text-gold" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>

                      <div className="flex justify-between">
                        <Button
                          onClick={prevQuestion}
                          disabled={currentQuestion === 0}
                          variant="outline"
                          className="flex items-center"
                        >
                          <ChevronRight
                            className={`w-4 h-4 ${isRTL ? "ml-1" : "mr-1"}`}
                          />
                          {t("candidates.quiz.buttons.previous")}
                        </Button>
                        <Button
                          onClick={nextQuestion}
                          disabled={!answers[currentQuestion]}
                          className="bg-gold hover:bg-gold-600 text-navy flex items-center"
                        >
                          {currentQuestion === questions.length - 1
                            ? t("candidates.quiz.buttons.getResults")
                            : t("candidates.quiz.buttons.next")}
                          <ChevronLeft
                            className={`w-4 h-4 ${isRTL ? "mr-1" : "ml-1"}`}
                          />
                        </Button>
                      </div>
                    </>
                  )}

                  {/* Result State */}
                  {showResult && result && !showBooking && (
                    <div className="text-center">
                      <div className="flex justify-center mb-6">
                        {getResultIcon(result.title)}
                      </div>

                      <h3
                        className={`text-2xl font-bold mb-4 ${getResultColor(
                          result.title
                        )}`}
                      >
                        {result.title}
                      </h3>

                      <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                        {result.description}
                      </p>

                      <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-6 mb-6">
                        <h4 className="font-semibold text-navy dark:text-white mb-3">
                          {t("candidates.quiz.recommendation")}:
                        </h4>
                        <p className="text-slate-600 dark:text-slate-300">
                          {result.recommendation}
                        </p>
                      </div>

                      {result.additionalInfo &&
                        Array.isArray(result.additionalInfo) &&
                        result.additionalInfo.length > 0 && (
                          <div
                            className={`mb-6 ${
                              isRTL ? "text-right" : "text-left"
                            }`}
                          >
                            <h4
                              className={`font-semibold text-navy dark:text-white mb-3 ${
                                isRTL ? "text-right" : ""
                              }`}
                            >
                              {t("candidates.quiz.expectation")}:
                            </h4>
                            <ul className="space-y-2">
                              {result.additionalInfo.map((info, index) => (
                                <li
                                  key={index}
                                  className={`flex items-start ${
                                    isRTL ? "flex-row-reverse justify-end" : ""
                                  }`}
                                >
                                  <span
                                    className={`text-slate-600 dark:text-slate-300 text-sm ${
                                      isRTL ? "text-right" : ""
                                    }`}
                                  >
                                    {info}
                                  </span>
                                  <CheckCircle
                                    className={`w-4 h-4 text-gold mt-0.5 shrink-0 ${
                                      isRTL ? "mr-2" : "ml-2"
                                    }`}
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                      <div className="flex flex-col sm:flex-row gap-4">
                        <Button
                          onClick={handleConsultationClick}
                          className="bg-gold hover:bg-gold-600 text-navy flex-1"
                        >
                          <MessageCircle
                            className={`w-4 h-4 ${isRTL ? "ml-2" : "mr-2"}`}
                          />
                          {t("candidates.quiz.buttons.bookConsultation")}
                        </Button>
                        <Button
                          onClick={resetQuiz}
                          variant="outline"
                          className="border-gold text-gold hover:bg-gold hover:text-navy flex-1"
                        >
                          {t("candidates.quiz.buttons.retakeAssessment")}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Booking Form State */}
                  {showBooking && (
                    <div>
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-navy dark:text-white">
                          {t("candidates.booking.title")}
                        </h3>
                        <Button
                          onClick={() => {
                            setShowBooking(false);
                            if (showResult) {
                              // Stay in result state
                            } else {
                              resetQuiz();
                            }
                          }}
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      <BookingForm
                        treatmentType="All-On-4 Dental Implants"
                        onSuccess={() => {
                          setShowBooking(false);
                          resetQuiz();
                        }}
                      />
c                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
